/**
 * Normalized audit-row detail drawer.
 *
 * @copyright 2025 Glimbr Pty Ltd
 * @version 2026-04-27-v1
 */
import { Copy, ExternalLink, GitCompare, RotateCcw, X } from 'lucide-react';
import { Chip } from '../components/chip';
import {
    buildCloudflareLogsQuery,
    getAuditDiffRows,
    getAuditSource,
    getAuditTargetId
} from '../../lib/audit';
import type { AdminAuditEntry, AuditDiffRow } from '../../lib/types';

const historyTypes = new Set([
    'track',
    'tracks',
    'track_group',
    'group',
    'share',
    'public_page',
    'bucket',
    'comment',
    'settings',
    'support_ticket'
]);

export function AuditDetailPanel({
    entry,
    onClose,
    onOpenResourceHistory
}: {
    entry: AdminAuditEntry;
    onClose: () => void;
    onOpenResourceHistory?: (resourceType: string, resourceId: string) => void;
}) {
    const source = getAuditSource(entry);
    const targetId = getAuditTargetId(entry);
    const diffRows = source === 'action' ? getAuditDiffRows(entry) : [];
    const logsQuery = buildCloudflareLogsQuery(entry);
    const canOpenHistory = historyTypes.has(entry.target_type) && !!targetId;

    return (
        <>
            <header className="detail-panel-header">
                <div className="audit-drawer-icon" data-source={source}>
                    <GitCompare size={18} />
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                    <h2 className="name">{entry.action}</h2>
                    <p className="email">
                        {entry.target_type} · {targetId}
                    </p>
                    <span className="uid">{entry.id}</span>
                </div>
                <button className="close" onClick={onClose} aria-label="Close audit detail">
                    <X size={16} />
                </button>
            </header>

            <div className="detail-panel-chips">
                <Chip tone={source === 'action' ? 'purple' : 'info'}>{source}</Chip>
                <Chip
                    tone={
                        entry.severity === 'destructive'
                            ? 'danger'
                            : entry.severity === 'sensitive'
                              ? 'warning'
                              : 'muted'
                    }
                    dot
                >
                    {entry.severity}
                </Chip>
                {entry.restorable && (
                    <Chip tone={entry.is_undone ? 'muted' : 'success'}>
                        {entry.is_undone ? 'undone' : 'restorable'}
                    </Chip>
                )}
            </div>

            <section className="detail-panel-section">
                <h3>Normalized fields</h3>
                <dl className="kv-list">
                    <dt>Occurred</dt>
                    <dd>{entry.occurred_at ?? entry.ts}</dd>
                    <dt>Actor</dt>
                    <dd>{entry.actor || entry.actor_id || 'system'}</dd>
                    <dt>Actor ID</dt>
                    <dd className="mono">{entry.actor_id ?? '—'}</dd>
                    <dt>Target</dt>
                    <dd>
                        {entry.target_type} · <span className="mono">{targetId}</span>
                    </dd>
                    <dt>Reason</dt>
                    <dd>{entry.reason ?? '—'}</dd>
                    <dt>IP</dt>
                    <dd className="mono">{entry.ip || '—'}</dd>
                    <dt>User agent</dt>
                    <dd>{entry.user_agent ?? '—'}</dd>
                </dl>
            </section>

            <section className="detail-panel-section">
                <h3>Diagnostics</h3>
                <dl className="kv-list">
                    <dt>Request ID</dt>
                    <dd>
                        <Copyable value={entry.request_id} />
                    </dd>
                    <dt>CF ray</dt>
                    <dd>
                        <Copyable value={entry.cf_ray} />
                    </dd>
                </dl>
                {logsQuery ? (
                    <div className="diagnostics-query">
                        <span>{logsQuery}</span>
                        <button
                            className="btn btn-sm btn-ghost"
                            type="button"
                            onClick={() => void copyText(logsQuery)}
                        >
                            <Copy size={11} /> Copy
                        </button>
                    </div>
                ) : (
                    <p className="text-muted" style={{ fontSize: 12, margin: 0 }}>
                        No request correlation IDs were attached to this row.
                    </p>
                )}
                <p className="text-muted" style={{ fontSize: 11, margin: '8px 0 0' }}>
                    Cloudflare logs are operational telemetry, not permanent restore history.
                </p>
            </section>

            {diffRows.length > 0 && (
                <section className="detail-panel-section">
                    <h3>Before / after</h3>
                    <DiffTable rows={diffRows} />
                </section>
            )}

            {(entry.metadata || entry.changes) && (
                <section className="detail-panel-section">
                    <h3>Payload</h3>
                    {entry.metadata && <JsonBlock label="Metadata" value={entry.metadata} />}
                    {entry.changes && <JsonBlock label="Changes" value={entry.changes} />}
                </section>
            )}

            <footer className="detail-panel-footer">
                {entry.restorable && (
                    <a className="btn btn-primary" href={`/restore?action=${entry.id}`}>
                        <RotateCcw size={13} /> Open in Restore Center
                    </a>
                )}
                {entry.actor_id && (
                    <a className="btn btn-ghost" href={`/users?user=${entry.actor_id}`}>
                        <ExternalLink size={13} /> Actor user detail
                    </a>
                )}
                {canOpenHistory && onOpenResourceHistory && (
                    <button
                        className="btn btn-ghost"
                        type="button"
                        onClick={() => onOpenResourceHistory(entry.target_type, targetId)}
                    >
                        <GitCompare size={13} /> Resource history
                    </button>
                )}
            </footer>
        </>
    );
}

export function DiffTable({ rows }: { rows: AuditDiffRow[] }) {
    if (!rows.length) {
        return <p className="text-muted">No field-level diff was included for this row.</p>;
    }

    return (
        <div className="audit-diff-list">
            {rows.map((row) => (
                <div className="audit-diff-row" key={row.field}>
                    <div className="audit-diff-field">{row.field}</div>
                    <pre>{row.before}</pre>
                    <pre>{row.after}</pre>
                </div>
            ))}
        </div>
    );
}

function JsonBlock({ label, value }: { label: string; value: unknown }) {
    return (
        <details className="json-block">
            <summary>{label}</summary>
            <pre>{JSON.stringify(value, null, 2)}</pre>
        </details>
    );
}

function Copyable({ value }: { value?: string | null }) {
    if (!value) return <>—</>;
    return (
        <button className="copyable-id" type="button" onClick={() => void copyText(value)}>
            <span>{value}</span>
            <Copy size={11} />
        </button>
    );
}

async function copyText(value: string) {
    await navigator.clipboard?.writeText(value);
}
